import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'

import type { XeroSyncJobRunDetailPayload, XeroSyncJobRunStepPayload } from '@ankor-io/common/types/integrations/xero/types'

import { organizationKeys } from '@/queries/organization'

type SSEEvent =
  | { run: XeroSyncJobRunDetailPayload }
  | { step: XeroSyncJobRunStepPayload }

/**
 * Connects to the run SSE endpoint when the run is in progress.
 * Updates React Query cache for both run detail and run steps queries.
 */
export function useRunSSE(
  orgId: string | undefined,
  connectionId: string | undefined,
  jobId: string | undefined,
  runId: string | undefined,
  enabled: boolean,
) {
  const queryClient = useQueryClient()
  const { getAccessToken } = useKindeAuth()
  const abortRef = useRef<AbortController | null>(null)

  useEffect(() => {
    if (!enabled || !orgId || !connectionId || !jobId || !runId) return

    const abortController = new AbortController()
    abortRef.current = abortController

    async function connect() {
      let token: string | undefined
      try {
        token = await getAccessToken()
      } catch {
        return
      }

      const url = `/api/lighthouse/organizations/${orgId}/integrations/xero/connections/${connectionId}/jobs/${jobId}/runs/${runId}/listen`

      let response: Response
      try {
        response = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
          signal: abortController.signal,
        })
      } catch {
        return
      }

      const contentType = response.headers.get('content-type') ?? ''

      // JSON response means the run is already completed or failed — refetch to get latest state
      if (contentType.includes('application/json')) {
        queryClient.invalidateQueries({
          queryKey: organizationKeys.xeroSyncJobRun(orgId, connectionId, jobId, runId),
        })
        return
      }

      // Parse SSE stream
      const reader = response.body?.getReader()
      if (!reader) return

      const decoder = new TextDecoder()
      let buffer = ''

      try {
        while (true) {
          const { value, done } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })

          // SSE events are separated by double newlines
          const parts = buffer.split('\n\n')
          // Keep the last incomplete part in the buffer
          buffer = parts.pop() ?? ''

          for (const part of parts) {
            const dataLine = part
              .split('\n')
              .find((line) => line.startsWith('data:'))
            if (!dataLine) continue

            const jsonStr = dataLine.slice(5).trim()
            if (!jsonStr) continue

            let event: SSEEvent
            try {
              event = JSON.parse(jsonStr)
            } catch {
              continue
            }

            if ('run' in event && event.run) {
              queryClient.setQueryData(
                organizationKeys.xeroSyncJobRun(orgId, connectionId, jobId, runId),
                event.run,
              )
            } else if ('step' in event && event.step) {
              // Update all step type caches — we don't know which type query is active
              const step = event.step as XeroSyncJobRunStepPayload
              const stepType = step.type

              const stepsKey = organizationKeys.xeroSyncJobRunSteps(
                orgId,
                connectionId,
                jobId,
                runId,
                stepType,
              )

              queryClient.setQueryData<XeroSyncJobRunStepPayload[]>(stepsKey, (old) => {
                if (!old) return [step]
                const idx = old.findIndex((s) => s.id === step.id)
                if (idx >= 0) {
                  // Update existing step
                  const updated = [...old]
                  updated[idx] = step
                  return updated
                }
                // Append new step
                return [...old, step]
              })
            }
          }
        }
      } catch (err) {
        // AbortError is expected on cleanup
        if (err instanceof DOMException && err.name === 'AbortError') return
      }

      // Stream ended — invalidate to refetch final state
      queryClient.invalidateQueries({
        queryKey: organizationKeys.xeroSyncJobRun(orgId, connectionId, jobId, runId),
      })
    }

    connect()

    return () => {
      abortController.abort()
      abortRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, connectionId, jobId, runId, enabled])
}
